import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaChevronDown,
  FaCoins,
  FaBuilding,
  FaExchangeAlt,
  FaUsers,
  FaMobileAlt,
  FaChartLine,
  FaShieldAlt,
  FaFileInvoiceDollar,
  FaCalculator,
  FaUserTie,
  FaClock,
  FaProjectDiagram,
  FaCertificate,
  FaNetworkWired,
} from 'react-icons/fa';
import Logo from './Logo';
import { IconType } from 'react-icons';

// Refactored navigation links into a more manageable structure
const navLinks: NavLinks = {
  market: {
    title: 'Market Segments',
    width: 'w-80',
    items: [
      { to: '/market-segments/stablecoin-issuers', icon: FaCoins, text: 'Stablecoin Issuers' },
      { to: '/market-segments/rwa-token-issuers', icon: FaBuilding, text: 'RWA Token Issuers' },
      { to: '/market-segments/exchanges', icon: FaExchangeAlt, text: 'Exchanges & Custodians' },
      { to: '/market-segments/daos', icon: FaUsers, text: 'DAOs' },
      { to: '/market-segments/payment-processors', icon: FaMobileAlt, text: 'Payment Processors' },
      { to: '/market-segments/crypto-fintechs', icon: FaChartLine, text: 'Crypto & Stablecoin Fintechs' },
    ],
  },
  audit: {
    title: 'Audit & Attest',
    width: 'w-80',
    items: [
      { to: '/services/financial-statement-audit', icon: FaFileInvoiceDollar, text: 'Financial Statement Audits', subtext: 'GAAP & IFRS compliant audits' },
      { to: '/services/internal-control-examinations', icon: FaShieldAlt, text: 'Internal Control Examinations', subtext: 'SOC 2, SOC 1 & custom reports' },
      { to: '/attestations', icon: FaCertificate, text: 'Attestation Services', subtext: 'Independent verification & assurance' },
    ],
  },
  accounting: {
    title: 'Accounting & Advisory',
    width: 'w-96',
    items: [
      { to: '/services/crypto-tax-services', icon: FaCalculator, text: 'Crypto Tax Services', subtext: 'Tax planning & compliance' },
      { to: '/services/outsourced-accounting', icon: FaUserTie, text: 'Outsourced Accounting & CFO', subtext: 'Strategic financial guidance' },
    ],
  },
  por: {
    title: 'Proof of Reserves',
    width: 'w-80',
    items: [
      { to: '/services/conventional-proof-of-reserves', icon: FaCertificate, text: 'Conventional Proof of Reserves', subtext: 'Traditional attestation' },
      { to: '/services/merkle-tree-proof-of-reserves', icon: FaProjectDiagram, text: 'Merkle Tree Proof of Reserves', subtext: 'Cryptographic verification' },
      { to: '/services/on-chain-proof-of-reserves', icon: FaNetworkWired, text: 'On-Chain Proof of Reserves', subtext: 'Smart contract verification' },
      { to: '/services/real-time-proof-of-reserves', icon: FaClock, text: 'Real-Time Proof of Reserves', subtext: 'Continuous monitoring' },
    ],
  },
  insights: {
    title: 'Insights',
    width: 'w-48',
    items: [{ to: '/blog', text: 'Blog' }],
  },
  about: {
    title: 'About Us',
    width: 'w-48',
    items: [
      { to: '/about', text: 'About Us' },
      { to: '/contact', text: 'Contact Us' },
    ],
  },
};

interface NavItem {
  to: string;
  text: string;
  icon?: IconType;
  subtext?: string;
}

interface NavLinks {
  [key: string]: {
    title: string;
    width: string;
    items: NavItem[];
  };
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" onClick={closeMenu}>
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            {Object.entries(navLinks).map(([key, { title, width, items }]) => (
              <div
                key={key}
                className="relative group"
                onMouseEnter={() => setActiveDropdown(key)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center space-x-1 text-sm text-gray-700 hover:text-accent transition-colors px-2 py-2 rounded-md">
                  <span>{title}</span>
                  <FaChevronDown className="text-xs" />
                </button>
                {activeDropdown === key && (
                  <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-0 pt-2 ${width}`}>
                    <div className="bg-white shadow-xl rounded-lg py-3 animate-slide-down border border-gray-100">
                      {items.map((item, index) => (
                        <Link
                          key={index}
                          to={item.to}
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-accent/5 hover:text-accent transition-all group"
                        >
                          {item.icon && <item.icon className="mr-3 text-accent group-hover:scale-110 transition-transform" />}
                          <div>
                            <div className="font-medium text-sm">{item.text}</div>
                            {item.subtext && <div className="text-xs text-gray-500">{item.subtext}</div>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact" className="btn-primary text-sm px-5 py-2">
              TALK TO AN EXPERT
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={toggleMenu} className="lg:hidden text-2xl text-primary p-2" aria-label="Toggle menu">
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-lg animate-slide-down max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-gray-100">
            <div className="container-custom py-4">
              <div className="flex flex-col space-y-2">
                {Object.entries(navLinks).map(([key, { title, items }]) => (
                  <div key={key} className="py-2">
                    <span className="text-gray-900 font-semibold text-sm uppercase tracking-wider px-4">{title}</span>
                    <div className="mt-2 flex flex-col space-y-1">
                      {items.map((item, index) => (
                        <Link
                          key={index}
                          to={item.to}
                          onClick={closeMenu}
                          className="flex items-center text-gray-700 hover:text-accent hover:bg-accent/5 transition-colors py-3 px-4 rounded-md"
                        >
                          {item.icon && <item.icon className="mr-3 text-accent text-base" />}
                          <span>{item.text}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
                <Link to="/contact" onClick={closeMenu} className="btn-primary w-full text-center mt-4">
                  TALK TO AN EXPERT
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;